<script setup lang="ts">
/**
 * フィードバック用ウィジェット。
 *
 * 他のコンポーネント・ストア・スタイル定義に依存しない自己完結コンポーネント。
 * このファイルを置いてどこかで描画するだけで動く。
 *
 * 1. Shift の2回押し(400ms以内)でフィードバックモードに入る
 *    (モード中は画面全体が選択対象になり、カーソルが十字になる)
 * 2. マウスを乗せた要素が赤い太線で囲まれ、クリックで選択が確定する
 * 3. コメントを書いて送信すると、画面共有APIでスクリーンショットを撮り
 *    メタデータ(uid / URL / DOMセレクタ)と一緒に backend へ送る
 * 4. Esc またはツールバーの「やめる」でモードを抜ける
 */
import { ref, watch, onMounted, onUnmounted } from 'vue'

type Rect = { top: number; left: number; width: number; height: number }

const props = withDefaults(
  defineProps<{
    // ログインユーザーのuid。呼び出し側から渡す(未ログインなら null)
    uid?: string | null
  }>(),
  { uid: null }
)

// 自分自身のDOMを選択対象・キャプチャ対象から除外するための目印
const ROOT_ATTR = 'data-feedback-widget'
const ENDPOINT = `${import.meta.env.VITE_APP_CLOUD_FUNCTION_URL}/internal_api/mawaru/dev`

const isActive = ref(false)
const hoverRect = ref<Rect | null>(null)
const selectedRect = ref<Rect | null>(null)
const selectedElement = ref<HTMLElement | null>(null)
const comment = ref('')
const isCapturing = ref(false)
const isSending = ref(false)
const errorMessage = ref('')
// 送信完了トースト
const isCompleted = ref(false)
let completedTimer: number | null = null

const toRect = (el: HTMLElement): Rect => {
  const r = el.getBoundingClientRect()
  return { top: r.top, left: r.left, width: r.width, height: r.height }
}

const isOwnElement = (el: Element | null): boolean =>
  !!el && !!el.closest(`[${ROOT_ATTR}]`)

const elementAtPoint = (x: number, y: number): HTMLElement | null => {
  const el = document.elementFromPoint(x, y)
  if (!el || isOwnElement(el)) return null
  return el as HTMLElement
}

/** 選択要素を指す CSS セレクタを組み立てる(id が見つかったらそこで打ち切り) */
const buildSelector = (element: HTMLElement): string => {
  const parts: string[] = []
  let current: Element | null = element
  while (current && current !== document.body && parts.length < 5) {
    if (current.id) {
      parts.unshift(`#${CSS.escape(current.id)}`)
      break
    }
    const tag = current.tagName.toLowerCase()
    const parent: HTMLElement | null = current.parentElement
    if (!parent) {
      parts.unshift(tag)
      break
    }
    const siblings = Array.from(parent.children).filter(
      (child) => child.tagName === current!.tagName
    )
    parts.unshift(
      siblings.length > 1
        ? `${tag}:nth-of-type(${siblings.indexOf(current) + 1})`
        : tag
    )
    current = parent
  }
  return parts.join(' > ')
}

const onMouseMove = (e: MouseEvent) => {
  // 選択確定後はホバー追従を止める
  if (selectedElement.value) return
  const el = elementAtPoint(e.clientX, e.clientY)
  hoverRect.value = el ? toRect(el) : null
}

const onClick = (e: MouseEvent) => {
  // ツールバーやコメント欄など、自分自身へのクリックはそのまま通す
  if (isOwnElement(e.target as Element)) return
  // フィードバックモード中はページ本来のクリックを発火させない
  e.preventDefault()
  e.stopPropagation()
  const el = elementAtPoint(e.clientX, e.clientY)
  if (!el) return
  selectedElement.value = el
  selectedRect.value = toRect(el)
  hoverRect.value = null
}

const onKeyDown = (e: KeyboardEvent) => {
  if (e.key === 'Escape') stop()
}

// Shift 2回押しでフィードバックモードをトグルする(モード外でも常に受け付ける)
const DOUBLE_SHIFT_INTERVAL_MS = 400
let lastShiftAt = 0

/** 文字入力中の連続 Shift を拾わないよう、入力欄にフォーカスがあるときは無効にする */
const isTypingElement = (el: EventTarget | null): boolean => {
  const target = el as HTMLElement | null
  if (!target?.tagName) return false
  return (
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) ||
    target.isContentEditable
  )
}

const onShortcutKeyDown = (e: KeyboardEvent) => {
  if (e.key !== 'Shift') {
    // Shift 以外を挟んだら 2 回押しの判定をリセットする
    lastShiftAt = 0
    return
  }
  // 押しっぱなしの自動リピートは無視する
  if (e.repeat) return
  if (isTypingElement(e.target)) {
    lastShiftAt = 0
    return
  }
  const now = e.timeStamp
  if (lastShiftAt > 0 && now - lastShiftAt <= DOUBLE_SHIFT_INTERVAL_MS) {
    lastShiftAt = 0
    if (isActive.value) {
      stop()
    } else {
      start()
    }
    return
  }
  lastShiftAt = now
}

// 押下中にフォーカスが外れたら判定をリセットする
const onWindowBlur = () => {
  lastShiftAt = 0
}

// スクロール・リサイズで選択枠がずれないよう追従させる
const syncRects = () => {
  if (selectedElement.value) {
    selectedRect.value = document.contains(selectedElement.value)
      ? toRect(selectedElement.value)
      : null
  }
  hoverRect.value = null
}

const start = () => {
  isActive.value = true
}

const clearSelection = () => {
  selectedElement.value = null
  selectedRect.value = null
  hoverRect.value = null
}

const stop = () => {
  isActive.value = false
  clearSelection()
  comment.value = ''
  errorMessage.value = ''
}

const addListeners = () => {
  document.addEventListener('mousemove', onMouseMove, true)
  document.addEventListener('click', onClick, true)
  document.addEventListener('keydown', onKeyDown, true)
  window.addEventListener('scroll', syncRects, true)
  window.addEventListener('resize', syncRects)
}

const removeListeners = () => {
  document.removeEventListener('mousemove', onMouseMove, true)
  document.removeEventListener('click', onClick, true)
  document.removeEventListener('keydown', onKeyDown, true)
  window.removeEventListener('scroll', syncRects, true)
  window.removeEventListener('resize', syncRects)
}

watch(isActive, (active) => {
  if (active) {
    addListeners()
  } else {
    removeListeners()
  }
  // モード中であることが分かるよう画面全体のカーソルを十字にする
  document.body.style.cursor = active ? 'crosshair' : ''
})

onMounted(() => {
  document.addEventListener('keydown', onShortcutKeyDown, true)
  window.addEventListener('blur', onWindowBlur)
})
onUnmounted(() => {
  removeListeners()
  document.removeEventListener('keydown', onShortcutKeyDown, true)
  window.removeEventListener('blur', onWindowBlur)
  document.body.style.cursor = ''
  if (completedTimer !== null) window.clearTimeout(completedTimer)
})

/**
 * 画面共有APIで現在のタブを1フレームだけキャプチャする。
 * ユーザーが共有を拒否した場合は null を返し、コメントだけ送信する。
 */
const captureScreenshot = async (): Promise<string | null> => {
  if (!navigator.mediaDevices?.getDisplayMedia) return null
  let stream: MediaStream | null = null
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      // 現在のタブを既定の共有対象にする(未対応ブラウザでは無視される)
      video: { displaySurface: 'browser' },
      audio: false,
      preferCurrentTab: true
    } as DisplayMediaStreamOptions)
    const video = document.createElement('video')
    video.srcObject = stream
    video.muted = true
    await video.play()
    // 最初のフレームが描画されるのを待つ
    await new Promise((resolve) => requestAnimationFrame(resolve))
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    canvas.getContext('2d')?.drawImage(video, 0, 0)
    video.pause()
    video.srcObject = null
    // data URL のヘッダを外し base64 本体のみにする
    return canvas.toDataURL('image/png').split(',')[1] ?? null
  } catch {
    return null
  } finally {
    stream?.getTracks().forEach((track) => track.stop())
  }
}

const COMPLETED_DURATION_MS = 4000

/** 送信できたことが分かるようトーストを一定時間出す */
const showCompleted = () => {
  isCompleted.value = true
  if (completedTimer !== null) window.clearTimeout(completedTimer)
  completedTimer = window.setTimeout(() => {
    isCompleted.value = false
    completedTimer = null
  }, COMPLETED_DURATION_MS)
}

const submit = async () => {
  if (isSending.value || comment.value.trim() === '') return
  errorMessage.value = ''
  isSending.value = true
  // キャプチャに自分のUIが写り込まないよう、赤枠だけ残して隠す
  isCapturing.value = true
  const screenshot = await captureScreenshot()
  isCapturing.value = false

  try {
    const response = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        prompt: comment.value,
        metadata: {
          uid: props.uid,
          url: window.location.href,
          selector: selectedElement.value
            ? buildSelector(selectedElement.value)
            : null
        },
        screenshot: screenshot
          ? { name: 'screenshot.png', mimeType: 'image/png', data: screenshot }
          : null
      })
    })
    if (!response.ok) {
      throw new Error(`status ${response.status}: ${await response.text()}`)
    }
    stop()
    showCompleted()
  } catch (error) {
    console.error('[FeedbackWidget] 送信に失敗しました', error)
    errorMessage.value = `送信に失敗しました: ${
      error instanceof Error ? error.message : error
    }`
  } finally {
    isSending.value = false
  }
}

const frameStyle = (rect: Rect) => ({
  top: `${rect.top}px`,
  left: `${rect.left}px`,
  width: `${rect.width}px`,
  height: `${rect.height}px`
})
</script>

<template>
  <Teleport to="body">
    <div data-feedback-widget>
      <template v-if="isActive">
        <div v-if="!isCapturing" class="feedback-toolbar">
          <span class="feedback-toolbar-text">
            {{
              selectedElement
                ? '要素を選択しました'
                : 'フィードバックしたい要素をクリックしてください'
            }}
          </span>
          <button
            v-if="selectedElement"
            type="button"
            class="feedback-toolbar-button"
            @click="clearSelection"
          >
            選び直す
          </button>
          <button type="button" class="feedback-toolbar-button" @click="stop">
            やめる（Esc）
          </button>
        </div>

        <div
          v-if="hoverRect"
          class="feedback-frame feedback-frame-hover"
          :style="frameStyle(hoverRect)"
        />
        <div
          v-if="selectedRect"
          class="feedback-frame feedback-frame-selected"
          :style="frameStyle(selectedRect)"
        />

        <div v-if="selectedElement && !isCapturing" class="feedback-panel">
          <textarea
            v-model="comment"
            class="feedback-panel-textarea"
            rows="4"
            placeholder="気になった点を書いてください"
          />
          <p v-if="errorMessage" class="feedback-panel-error">
            {{ errorMessage }}
          </p>
          <p class="feedback-panel-note">
            送信時に画面共有の許可を求めます。許可するとスクリーンショットが添付されます。
          </p>
          <div class="feedback-panel-actions">
            <button
              type="button"
              class="feedback-panel-cancel"
              :disabled="isSending"
              @click="stop"
            >
              キャンセル
            </button>
            <button
              type="button"
              class="feedback-panel-submit"
              :disabled="isSending || comment.trim() === ''"
              @click="submit"
            >
              {{ isSending ? '送信中…' : '送信' }}
            </button>
          </div>
        </div>
      </template>

      <div v-if="isCompleted" class="feedback-toast" role="status">
        <span class="feedback-toast-check">✓</span>
        フィードバックを送信しました
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
/* 他のスタイル定義に依存しない自己完結コンポーネントのため、ここに閉じて定義する */
.feedback-toolbar {
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2147483001;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  font-size: 13px;
  color: #fff;
  background: rgba(20, 20, 20, 0.92);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.24);
}

.feedback-toolbar-text {
  white-space: nowrap;
}

.feedback-toolbar-button {
  padding: 4px 10px;
  font-size: 12px;
  color: #fff;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 4px;
  cursor: pointer;
}

.feedback-frame {
  position: fixed;
  z-index: 2147483000;
  box-sizing: content-box;
  pointer-events: none;
  border: 3px solid #e02020;
  border-radius: 2px;
  /* 要素の外側に枠を出す */
  margin: -3px 0 0 -3px;
}

.feedback-frame-hover {
  border-style: dashed;
  opacity: 0.7;
}

.feedback-frame-selected {
  border-width: 4px;
  margin: -4px 0 0 -4px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.12);
}

.feedback-panel {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 2147483001;
  box-sizing: border-box;
  width: 320px;
  padding: 16px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.24);
}

.feedback-panel-textarea {
  box-sizing: border-box;
  width: 100%;
  padding: 8px;
  font-family: inherit;
  font-size: 13px;
  color: #333;
  border: 1px solid #ccc;
  border-radius: 4px;
  resize: vertical;
}

.feedback-panel-error {
  margin: 8px 0 0;
  font-size: 12px;
  color: #e02020;
}

.feedback-panel-note {
  margin: 8px 0 0;
  font-size: 11px;
  line-height: 1.5;
  color: #888;
}

.feedback-panel-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
}

.feedback-panel-cancel {
  padding: 6px 12px;
  font-size: 13px;
  color: #555;
  background: transparent;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
}

.feedback-panel-submit {
  padding: 6px 16px;
  font-size: 13px;
  font-weight: bold;
  color: #fff;
  background: #e02020;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.feedback-panel-submit:disabled,
.feedback-panel-cancel:disabled {
  opacity: 0.5;
  cursor: default;
}

.feedback-toast {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 2147483001;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: bold;
  color: #fff;
  background: rgba(20, 20, 20, 0.92);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.24);
  animation: feedback-toast-in 0.2s ease-out;
}

.feedback-toast-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  font-size: 12px;
  color: #fff;
  background: #2bab6a;
  border-radius: 50%;
}

@keyframes feedback-toast-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
